import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { BsPencil, BsPlusLg, BsTrash } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { usePagination, useTable } from "react-table";
import Loader from "../../components/Loader";
import Api from "../../service/http";

const Courses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const fetchCourses = async (searchTerm = '', page = 1) => {
    setLoading(true);
    try {
      const response = await Api.get(`web/courses?page=${page}&search=${searchTerm}`);
      const data = response.data;
      setCourses(data.data);
      setPageCount(data.last_page);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(debounce((value, page) => {
    fetchCourses(value, page);
  }, 500), []);

  useEffect(() => {
    debouncedFetch(search, pagination.pageIndex + 1);
  }, [search, pagination.pageIndex]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleDelete = async () => {
    try {
      await Api.get(`web/course-delete/${deleteId}`);
        alert("Course deleted successfully!");
      setDeleteId(null);
      setShowConfirm(false);
      fetchCourses(search, pagination.pageIndex + 1);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const columns = useMemo(() => [
    { 
      Header: "Image", 
      accessor: "image", 
      Cell: ({ value }) => (
        <img 
          src={value} 
          alt="Course" 
          className="h-10 w-16 sm:h-12 sm:w-20 object-cover rounded-lg shadow-sm border border-gray-100 bg-gray-50" 
        />
      ) 
    },
    { 
      Header: "Name", 
      accessor: "name", 
      Cell: ({ value }) => (
        <div className="flex flex-col">
          <span className="font-bold text-gray-900 line-clamp-1">{value}</span>
        </div>
      ) 
    },
    { 
      Header: "Description", 
      accessor: "description", 
      Cell: ({ value }) => (
        <span className="text-gray-500 text-sm line-clamp-2 max-w-[200px] sm:max-w-xs md:max-w-md">
          {value || "No description provided"}
        </span>
      ) 
    },
    {
      Header: "Actions",
      id: "actions",
      Cell: ({ row }) => (
        <div className="flex items-center space-x-1 sm:space-x-2">
          <button 
            onClick={() => navigate(`/courses/edit/${row.original.id}`)}
            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all active:scale-95"
            title="Edit Course"
          >
            <BsPencil className="text-lg" />
          </button>
          <button 
            onClick={() => { setDeleteId(row.original.id); setShowConfirm(true); }}
            className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all active:scale-95"
            title="Delete Course"
          >
            <BsTrash className="text-lg" />
          </button>
        </div>
      )
    },
  ], [navigate]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    state: tableState,
  } = useTable(
    { columns, data: courses, manualPagination: true, pageCount, initialState: { pageIndex: pagination.pageIndex } },
    usePagination
  );

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: tableState.pageIndex }));
  }, [tableState.pageIndex]);

  return (
    <div className="p-0 h-full overflow-y-auto scrollbar-hide bg-gray-50/50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
            <p className="text-gray-500 text-sm mt-1">Manage and organize your courses efficiently.</p>
          </div>
          <button
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100"
            onClick={() => navigate('/courses/add')}
          >
            <BsPlusLg />
            Add Course
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/30">
            <div className="relative max-w-md">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search courses by name or description..."
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table {...getTableProps()} className="w-full">
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()} className="bg-gray-50/50">
                    {headerGroup.headers.map(column => (
                      <th {...column.getHeaderProps()} className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-20">
                      <Loader className="mx-auto" />
                    </td>
                  </tr>
                ) : page.length > 0 ? (
                  page.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50/50 transition-colors">
                        {row.cells.map(cell => (
                          <td {...cell.getCellProps()} className="px-6 py-4 text-sm text-gray-600">
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                          <BiSearch className="text-2xl text-gray-300" />
                        </div>
                        <p>No courses found matching your criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
            <div className="text-sm text-gray-500 font-medium">
              Showing page <span className="text-gray-900">{pagination.pageIndex + 1}</span> of <span className="text-gray-900">{pageCount}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
              >
                Previous
              </button>
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Delete Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden transform transition-all">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <BsTrash className="text-2xl text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Course</h3>
              <p className="text-gray-500 text-sm">
                Are you sure you want to delete this course? This action cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all text-sm shadow-md shadow-red-100"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;
